import type { FetchBaseQueryError } from '@reduxjs/toolkit/query'

/**
 * Type predicate to narrow an unknown error to `FetchBaseQueryError`
 */
export function isFetchBaseQueryError(error: unknown): error is FetchBaseQueryError & {
  queryData?: Record<string, unknown>
  data?: unknown
} {
  return typeof error === 'object' && error !== null && 'status' in error
}

/**
 * Type predicate to narrow an unknown error to an object with a string 'message' property
 */
export function isErrorWithMessage(error: unknown): error is { message: string; name?: unknown } {
  return typeof error === 'object' && error !== null && 'message' in error && typeof error.message === 'string'
}

export function isErrorWithStack(error: unknown): error is { stack: string; name?: unknown } {
  return typeof error === 'object' && error !== null && 'stack' in error && typeof error.stack === 'string'
}

export function isErrorWithCause(error: unknown): error is { cause: unknown; name?: unknown } {
  return typeof error === 'object' && error !== null && 'cause' in error
}

export function isErrorWithName(error: unknown): error is { name: string } {
  return typeof error === 'object' && error !== null && 'name' in error && typeof error.name === 'string'
}

export function isErrorWithData(error: unknown): error is { data: string } {
  return typeof error === 'object' && error !== null && 'data' in error && typeof error.data === 'string'
}

interface LambdaError {
  error: {
    status?: number
    title?: string
    type?: string
    description: string
    metadata?: {
      source?: string
    }
  }
}

export function isApiError(error: unknown): error is LambdaError {
  return (
    typeof error === 'object' &&
    error !== null &&
    'error' in error &&
    typeof error.error === 'object' &&
    error.error !== null &&
    'description' in error.error &&
    typeof error.error.description === 'string' &&
    ('status' in error.error ? typeof error.error.status === 'number' : true) &&
    ('title' in error.error ? typeof error.error.title === 'string' : true) &&
    ('type' in error.error ? typeof error.error.type === 'string' : true)
  )
}

interface EcmError {
  statusCode: number
  error: string
  correlationId: string
  dateTime: string
}

function isEcmError(error: unknown): error is EcmError {
  return (
    typeof error === 'object' &&
    error !== null &&
    'statusCode' in error &&
    typeof error.statusCode === 'number' &&
    'error' in error &&
    typeof error.error === 'string'
  )
}

export class AppError extends Error {
  readonly cause: unknown
  readonly data: unknown

  constructor(name: string | undefined, message: string | undefined, error?: unknown, data?: unknown) {
    super(message)

    this.name = name ?? 'AppError'
    this.message = message ?? 'An error occurred'
    this.cause = error
    this.data = data
  }
}

function combineMessage(...messages: (string | null | undefined | number)[]) {
  return messages.filter((m) => m !== undefined && m !== null).join('\n')
}

const mapStatusToMessage: Record<number, string | undefined> = {
  503: 'Service unavailable',
  504: 'Gateway timeout',
  502: 'Bad gateway',
  501: 'Not implemented',
  500: 'Internal Server Error',
  429: 'Too many requests',
  418: "I'm a teapot",
  417: 'Expectation failed',
  416: 'Range not satisfiable',
  415: 'Unsupported media type',
  414: 'URI too long',
  413: 'Payload too large',
  412: 'Precondition failed',
  411: 'Length required',
  410: 'Gone',
  409: 'Conflict',
  408: 'Request timeout',
  407: 'Proxy authentication required',
  406: 'Not acceptable',
  405: 'Method not allowed',
  404: 'Not found',
  403: 'Forbidden',
  402: 'Payment required',
  401: 'Unauthorized',
  400: 'Bad request',
  307: 'Temporary redirect',
  306: 'Switch proxy',
  305: 'Use proxy',
  304: 'Not modified',
  303: 'See other',
  302: 'Found',
  301: 'Moved permanently',
  300: 'Multiple choices',
}

type ErrorSource = 'app' | 'network' | 'api' | 'ecm' | 'unknown'

export interface ErrorDescription {
  source: ErrorSource
  name: string
  message?: string
  stack?: string
  data?: string
}

export const flattenError = (error: unknown): ErrorDescription[] => {
  if (typeof error === 'string') {
    return [{ name: error, source: 'app' }]
  }

  if (error instanceof AppError) {
    const cause = error.cause ? flattenError(error.cause) : []
    return [
      ...cause,
      {
        name: error.name,
        message: error.message,
        stack: error.stack,
        source: 'app',
        data: 'data' in error ? JSON.stringify(error.data, null, 2) : undefined,
      },
    ]
  }

  if (isFetchBaseQueryError(error)) {
    const underlyingError: ErrorDescription[] = []

    if (isApiError(error.data)) {
      const rawApiError = error.data.error
      const withEcmMessage = rawApiError.title || rawApiError.type || 'Lambda execution failed'
      const standaloneMessage = rawApiError.description || withEcmMessage
      const apiErrorInfo = {
        name: [rawApiError.status, 'API Error'].filter(Boolean).join(': '),
        message: standaloneMessage,
        stack: rawApiError.metadata?.source,
        source: 'api',
        data: JSON.stringify(rawApiError, null, 2),
      } as ErrorDescription

      try {
        const parsedError = JSON.parse(rawApiError.description) as unknown

        if (isEcmError(parsedError)) {
          underlyingError.push({
            name: `${parsedError.statusCode}: ECM Error`,
            message: parsedError.error,
            source: 'ecm',
            data: JSON.stringify(parsedError, null, 2),
          })
          apiErrorInfo.message = withEcmMessage
        }

        underlyingError.push(apiErrorInfo)
      } catch {
        underlyingError.push(apiErrorInfo)
      }
    }

    const data = error.data
      ? JSON.stringify(
          {
            data: error.data,
            ...error.queryData,
          },
          null,
          2,
        )
      : JSON.stringify(error.queryData, null, 2)
    if (error.status === 'TIMEOUT_ERROR') {
      return [
        ...underlyingError,
        {
          name: 'Request timed out',
          message: error.error,
          source: 'network',
          data,
        },
      ]
    }
    if (error.status === 'FETCH_ERROR') {
      return [...underlyingError, { name: 'Network error', message: error.error, source: 'network', data }]
    }
    if (error.status === 'PARSING_ERROR') {
      return [
        ...underlyingError,
        {
          name: `${error.originalStatus} Parsing error`,
          message: error.error,
          source: 'network',
          data,
        },
      ]
    }
    if (error.status === 'CUSTOM_ERROR') {
      return [...underlyingError, { name: 'Custom Fetch error', message: error.error, source: 'app', data }]
    }
    return [
      ...underlyingError,
      {
        name: `${error.status}: Network Error`,
        source: 'network',
        message: mapStatusToMessage[error.status] ? `${mapStatusToMessage[error.status]}` : String(error.status),
        data,
      },
    ]
  }

  if (error instanceof Error) {
    return [
      { name: error.name, message: error.message, stack: error.stack, source: 'app', data: 'data' in error ? JSON.stringify(error.data) : undefined },
    ]
  }

  if (isSerializedError(error)) {
    const stack = error.stack
    const name = error.name
    const message = error.message
    const cause = error.cause ? flattenError(error.cause) : []
    const data = error.data
    const source: ErrorSource = error.source ?? 'app'
    return [...cause, { name, message, stack, data, source } satisfies ErrorDescription]
  }

  return [{ name: 'Unknown Error', message: 'An error occurred', source: 'app', data: JSON.stringify(error, null, 2) }]
}

export interface SerializedError {
  source: ErrorSource
  name: string
  message?: string
  stack?: string
  data?: string
  cause?: SerializedError
}

function isSerializedError(error: unknown): error is SerializedError {
  return (
    typeof error === 'object' &&
    error !== null &&
    'name' in error &&
    typeof error.name === 'string' &&
    'message' in error &&
    (typeof error.message === 'string' || typeof error.message === 'undefined') &&
    'stack' in error &&
    (typeof error.stack === 'string' || typeof error.stack === 'undefined') &&
    'data' in error &&
    (typeof error.data === 'string' || typeof error.data === 'undefined') &&
    'cause' in error &&
    (error.cause === undefined || isSerializedError(error.cause))
  )
}

export function makeSerializedError(error: unknown): SerializedError | undefined {
  const flattedError = flattenError(error)
  if (flattedError.length === 0) {
    return undefined
  }

  let result: SerializedError | undefined
  for (let i = 0; i < flattedError.length; i++) {
    const cause: SerializedError | undefined = result

    result = {
      source: flattedError[i].source,
      name: flattedError[i].name,
      message: flattedError[i].message,
      stack: flattedError[i].stack,
      data: flattedError[i].data,
      cause,
    }
  }

  if (result && !result.stack) {
    const stackHolder = { stack: '' }
    Error.captureStackTrace(stackHolder, makeSerializedError)
    result.stack = String(stackHolder.stack)
  }

  return result
}

export function wrapErrorLike(error: unknown, customMessage?: string): Error {
  if (error instanceof Error) {
    error.message = combineMessage(customMessage, error.message)
    return error
  }

  if (isErrorWithMessage(error)) {
    return new AppError(typeof error.name === 'string' ? error.name : undefined, combineMessage(customMessage, error.message), error)
  }

  if (isFetchBaseQueryError(error)) {
    return new AppError('Query Error', customMessage ?? 'A fetch base query failed', error)
  }

  if (typeof error === 'string') {
    return new AppError('Generic Error', combineMessage(customMessage, error))
  }

  return new AppError('Unknown Error', customMessage ?? 'An error occurred', error)
}

export function errorToString(error: unknown): string {
  const flattenedError = flattenError(error)
  const errorMessages = flattenedError.map((e) => {
    return [e.name, e.message].filter(Boolean).join(': ')
  })
  return errorMessages.join('\n')
}
